import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, BarChart3, Download, Settings, Calendar, TrendingUp, Clock, FileText, Filter, Paperclip, X } from 'lucide-react';
import { ticketService } from '@/services/ticketService';
import {
  Ticket,
  TicketStage,
  TicketReason,
  CreateTicketDTO,
  TicketPriority,
  TicketMetrics,
  TicketFilter,
  EMPTY_TICKET_FILTER
} from '@/types/tickets';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DndContext, DragEndEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import TicketKanbanColumn from '@/components/tickets/TicketKanbanColumn';
import TicketFilterMenu from '@/components/tickets/TicketFilterMenu';
import { isFilterActive } from '@/lib/ticket-filter';
import TicketDetailsModal from '@/components/tickets/TicketDetailsModal';
import TicketNotificationsBell from '@/components/tickets/TicketNotificationsBell';
import { emitTicketNotificationsChanged } from '@/hooks/useTicketNotifications';
import MultiUserSelect from '@/components/tickets/MultiUserSelect';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Tickets = () => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [stages, setStages] = useState<TicketStage[]>([]);
  const [reasons, setReasons] = useState<TicketReason[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [globalFilter, setGlobalFilter] = useState<TicketFilter>(EMPTY_TICKET_FILTER);
  const [columnFilters, setColumnFilters] = useState<Record<number, TicketFilter>>({});

  // "Limpar filtros" do filtro global = reset geral (global + todas as colunas)
  const handleClearAllFilters = () => {
    setGlobalFilter(EMPTY_TICKET_FILTER);
    setColumnFilters({});
  };

  const anyColumnFilterActive = Object.values(columnFilters).some(isFilterActive);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'stages' | 'reasons'>('stages');

  // Inicializar filtros com datas padrão (últimos 30 dias)
  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      reason_id: '',
      client_id: ''
    };
  };

  const [reportFilters, setReportFilters] = useState(getDefaultDates());
  const [metrics, setMetrics] = useState<TicketMetrics | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [showStageForm, setShowStageForm] = useState(false);
  const [showReasonForm, setShowReasonForm] = useState(false);
  const [editingStage, setEditingStage] = useState<TicketStage | null>(null);
  const [editingReason, setEditingReason] = useState<TicketReason | null>(null);
  const [stageForm, setStageForm] = useState({ name: '', description: '', color: '#3B82F6', order_index: 0 });
  const [reasonForm, setReasonForm] = useState({ name: '', description: '' });

  // Form data
  const [newTicket, setNewTicket] = useState<CreateTicketDTO>({
    client_id: 0,
    reason_id: 0,
    title: '',
    description: '',
    priority: TicketPriority.MEDIUM,
    due_date: undefined,
    co_assignees: [] as number[],
  });
  const [clients, setClients] = useState<any[]>([]);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [newTicketFiles, setNewTicketFiles] = useState<File[]>([]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Carregar dados
  const loadData = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 [Tickets] Carregando dados...');

      // Não filtrar por status para mostrar todos os tickets (incluindo finalizados)
      const [stagesData, reasonsData, ticketsData, clientsData, usersResponse] = await Promise.all([
        ticketService.kanban(), // Removido 'open' para buscar todos os tickets
        ticketService.listReasons(),
        ticketService.listTickets(), // Removido filtro de status para buscar todos
        api.get('/clients').then((res: any) => Array.isArray(res) ? res : (res.data || res.clients || [])).catch(() => []),
        api.get('/auth/users').then((res: any) => {
          // O endpoint retorna { users: [...] }
          const users = Array.isArray(res) ? res : (res.users || res.data?.users || []);
          console.log('[Tickets] Usuários carregados:', users.length, users);
          return Array.isArray(users) ? users : [];
        }).catch((error) => {
          console.error('[Tickets] Erro ao carregar usuários:', error);
          toast({
            title: 'Aviso',
            description: 'Não foi possível carregar a lista de usuários. Tente novamente.',
            variant: 'destructive'
          });
          return [];
        })
      ]);

      // Normalizar stages e garantir que tickets estejam associados corretamente
      // IMPORTANTE: Garantir que IDs sejam sempre números para evitar problemas de comparação
      const normalizedStages = Array.isArray(stagesData)
        ? stagesData.map((stage: any) => ({
          ...stage,
          id: Number(stage.id), // Garantir que ID seja sempre número
          order_index: Number(stage.order_index) || 0,
          tickets: Array.isArray(stage.tickets) ? stage.tickets.map((ticket: any) => {
            // Debug log para tickets em "Em Análise"
            if (stage.name === 'Em Análise') {
              console.log(`[Tickets.loadData] Ticket ${ticket.id} - Dados recebidos do backend:`, {
                resolution_time_hours: ticket.resolution_time_hours,
                resolution_time_set_at: ticket.resolution_time_set_at,
                ticket_keys: Object.keys(ticket)
              });
            }

            return {
              ...ticket,
              id: Number(ticket.id),
              stage_id: Number(stage.id), // Garantir que stage_id seja número
              client_id: Number(ticket.client_id) || ticket.client_id,
              reason_id: Number(ticket.reason_id) || ticket.reason_id,
              assigned_to: ticket.assigned_to ? Number(ticket.assigned_to) : null,
              // Preservar is_viewed se existir (pode ser boolean, null ou undefined)
              is_viewed: ticket.is_viewed !== undefined ? (ticket.is_viewed === true || ticket.is_viewed === 'true') : undefined,
              // Preservar explicitamente os campos de resolução de tempo
              resolution_time_hours: ticket.resolution_time_hours ?? ticket.resolutionTimeHours ?? null,
              resolution_time_set_at: ticket.resolution_time_set_at ?? ticket.resolutionTimeSetAt ?? null,
              resolution_time_paused_at: ticket.resolution_time_paused_at ?? ticket.resolutionTimePausedAt ?? null,
              remaining_hours: ticket.remaining_hours ?? null,
              // Preservar explicitamente o campo moved_to_stage_at
              moved_to_stage_at: ticket.moved_to_stage_at ?? null
            };
          }) : []
        }))
        : [];

      // Garantir que todos os IDs sejam números válidos
      const finalStages = normalizedStages.map(stage => {
        const stageId = Number(stage.id);
        if (isNaN(stageId)) {
          console.warn('⚠️ [Tickets] Stage com ID inválido:', stage);
          return stage;
        }
        return {
          ...stage,
          id: stageId // Garantir que é número
        };
      });

      console.log('📊 [Tickets] Stages normalizados:', finalStages.map(s => ({
        id: s.id,
        idType: typeof s.id,
        name: s.name,
        ticketsCount: s.tickets?.length || 0
      })));

      setStages(finalStages);
      setReasons(Array.isArray(reasonsData) ? reasonsData : []);

      // Extrair tickets dos stages (kanban retorna stages com tickets incluídos)
      // IMPORTANTE: Manter referência aos tickets para filtragem posterior
      let ticketsList: Ticket[] = [];
      normalizedStages.forEach((stage: any) => {
        if (stage.tickets && Array.isArray(stage.tickets)) {
          // Normalizar cada ticket preservando is_viewed e assigned_to
          const normalizedTickets = stage.tickets.map((ticket: any) => ({
            ...ticket,
            id: Number(ticket.id),
            stage_id: Number(ticket.stage_id) || Number(stage.id),
            assigned_to: ticket.assigned_to ? Number(ticket.assigned_to) : null,
            // Preservar is_viewed se existir (pode ser boolean, null ou undefined)
            is_viewed: ticket.is_viewed !== undefined ? (ticket.is_viewed === true || ticket.is_viewed === 'true') : undefined,
            // Preservar explicitamente os campos de resolução de tempo
            resolution_time_hours: ticket.resolution_time_hours ?? ticket.resolutionTimeHours ?? null,
            resolution_time_set_at: ticket.resolution_time_set_at ?? ticket.resolutionTimeSetAt ?? null,
            resolution_time_paused_at: ticket.resolution_time_paused_at ?? ticket.resolutionTimePausedAt ?? null,
            remaining_hours: ticket.remaining_hours ?? null,
            // Preservar explicitamente o campo moved_to_stage_at
            moved_to_stage_at: ticket.moved_to_stage_at ?? null
          }));
          ticketsList = [...ticketsList, ...normalizedTickets];
        }
      });

      // Fallback: se não vier nos stages, tentar buscar em ticketsData
      if (ticketsList.length === 0) {
        if (Array.isArray(ticketsData?.tickets)) {
          ticketsList = ticketsData.tickets;
        } else if (Array.isArray(ticketsData)) {
          ticketsList = ticketsData;
        }
      }

      console.log('✅ [Tickets] Tickets carregados:', {
        total: ticketsList.length,
        stagesCount: normalizedStages.length,
        byStage: normalizedStages.reduce((acc: any, stage: any) => {
          acc[stage.name] = {
            count: stage.tickets?.length || 0,
            stageId: stage.id,
            ticketIds: stage.tickets?.map((t: any) => t.id) || []
          };
          return acc;
        }, {}),
        tickets: ticketsList.map((t: any) => ({
          id: t.id,
          title: t.title?.substring(0, 30),
          stage_id: t.stage_id,
          stageIdType: typeof t.stage_id,
          stage: t.stage?.name,
          status: t.status
        }))
      });
      setTickets(ticketsList);
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setUsers(Array.isArray(usersResponse) ? usersResponse : []);
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Erro ao carregar dados',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      console.log('⚠️ [Tickets] Drag end sem destino válido');
      return;
    }

    const ticketId = active.id as number;
    const overIdStr = String(over.id);

    console.log('🔍 [Tickets] Iniciando drag end:', {
      overId: over.id,
      overIdType: typeof over.id,
      overIdStr,
      activeId: active.id,
      stagesCount: stages.length,
      stages: stages.map(s => ({ id: s.id, name: s.name, idType: typeof s.id }))
    });

    // Extrair stageId de forma robusta
    let stageId: number | null = null;

    // Verificar se é uma stage (formato "stage-{id}")
    if (overIdStr.startsWith('stage-')) {
      const extractedId = overIdStr.replace('stage-', '').trim();
      const numId = Number(extractedId);
      if (!isNaN(numId) && numId > 0) {
        stageId = numId;
        console.log('✅ [Tickets] StageId extraído do formato stage-{id}:', stageId);
      } else {
        console.warn('⚠️ [Tickets] Falha ao converter ID extraído:', extractedId);
      }
    } else {
      // Se não começa com "stage-", pode ser um ticket (ID numérico)
      // Nesse caso, buscar o stage_id do ticket que está sendo usado como destino
      const overTicketId = Number(overIdStr);
      if (!isNaN(overTicketId) && overTicketId > 0) {
        // Verificar se é um ticket válido e pegar seu stage_id
        const overTicket = tickets.find(t => Number(t.id) === overTicketId);
        if (overTicket && overTicket.stage_id) {
          stageId = Number(overTicket.stage_id);
          console.log('✅ [Tickets] StageId obtido do ticket destino:', {
            ticketId: overTicketId,
            stageId: stageId,
            stageName: stages.find(s => Number(s.id) === stageId)?.name
          });
        } else {
          // Se não encontrou o ticket, verificar se o número corresponde a uma stage válida
          // Mas só fazer isso se o número estiver no range das stages disponíveis
          const isValidStageId = stages.some(s => Number(s.id) === overTicketId);
          if (isValidStageId) {
            stageId = overTicketId;
            console.log('✅ [Tickets] ID usado diretamente como stageId (é uma stage válida):', stageId);
          } else {
            console.warn('⚠️ [Tickets] ID não corresponde a ticket nem stage válida:', overTicketId);
            // Não definir stageId aqui, deixar como null para gerar erro apropriado
          }
        }
      }
    }

    // Validação: verificar se stageId é válido
    if (!stageId || isNaN(stageId) || stageId <= 0) {
      console.error('❌ [Tickets] StageId inválido:', {
        overId: over.id,
        overIdType: typeof over.id,
        overIdStr,
        extractedStageId: stageId,
        activeId: active.id,
        activeIdType: typeof active.id,
        stagesAvailable: stages.map(s => ({ id: s.id, name: s.name }))
      });
      toast({
        title: 'Erro',
        description: 'Não foi possível identificar a etapa de destino. Tente novamente.',
        variant: 'destructive'
      });
      return;
    }

    // Encontrar o ticket atual e a etapa de destino
    // IMPORTANTE: Usar comparação numérica robusta para evitar problemas de tipo
    const currentTicket = tickets.find(t => Number(t.id) === Number(ticketId));

    console.log('🔍 [Tickets] Buscando stage:', {
      stageId,
      stageIdType: typeof stageId,
      stages: stages.map(s => ({
        id: s.id,
        idType: typeof s.id,
        numericId: Number(s.id),
        name: s.name,
        willMatch: Number(s.id) === Number(stageId)
      }))
    });

    // Tentar encontrar a etapa usando múltiplas estratégias de comparação
    let targetStage = stages.find(s => {
      const sId = Number(s.id);
      const targetId = Number(stageId);
      // Comparação numérica estrita
      if (!isNaN(sId) && !isNaN(targetId)) {
        return sId === targetId;
      }
      // Fallback: comparação por string
      return s.id.toString() === stageId.toString() || s.id === stageId;
    });

    // Se ainda não encontrou, tentar busca mais permissiva com conversão explícita
    if (!targetStage) {
      const numericStageId = Number(stageId);
      if (!isNaN(numericStageId)) {
        // Tentar todas as formas possíveis de comparação
        targetStage = stages.find(s => {
          // Comparação direta
          if (s.id === stageId || s.id === numericStageId) return true;

          // Comparação numérica
          const sIdNum = Number(s.id);
          if (!isNaN(sIdNum) && sIdNum === numericStageId) return true;

          // Comparação por string
          if (String(s.id) === String(stageId)) return true;

          return false;
        });
      }
    }

    console.log('🎯 [Tickets] Resultado da busca:', {
      stageId,
      stageIdType: typeof stageId,
      targetStageFound: !!targetStage,
      targetStage: targetStage ? {
        id: targetStage.id,
        idType: typeof targetStage.id,
        name: targetStage.name
      } : null,
      stagesLength: stages.length,
      allStageIds: stages.map(s => ({ id: s.id, idType: typeof s.id, name: s.name }))
    });

    // Validação: verificar se a etapa existe
    if (!targetStage) {
      console.error('❌ [Tickets] Etapa não encontrada no estado local:', {
        stageId,
        stageIdType: typeof stageId,
        ticketId,
        ticketIdType: typeof ticketId,
        overId: over.id,
        overIdType: typeof over.id,
        overIdStr,
        availableStages: stages.map(s => ({
          id: s.id,
          idType: typeof s.id,
          name: s.name,
          numericId: Number(s.id),
          matches: Number(s.id) === Number(stageId),
          matchesString: s.id.toString() === stageId.toString(),
          matchesDirect: s.id === stageId
        })),
        allStageIds: stages.map(s => s.id),
        searchStageId: stageId,
        normalizedStageId: Number(stageId)
      });
      toast({
        title: 'Erro',
        description: 'Etapa não encontrada. Recarregue a página e tente novamente.',
        variant: 'destructive'
      });
      return;
    }

    console.log('🔄 [Tickets] Movendo ticket:', {
      ticketId,
      ticketTitle: currentTicket?.title,
      fromStage: currentTicket?.stage?.name,
      toStageId: stageId,
      toStageName: targetStage.name,
    });

    try {
      const updatedTicket = await ticketService.moveTicketStage(ticketId, stageId);

      console.log('✅ [Tickets] Ticket movido com sucesso:', {
        ticketId: updatedTicket.id,
        newStageId: updatedTicket.stage_id,
        newStatus: updatedTicket.status,
      });

      // Recarregar dados para atualizar o kanban
      await loadData();

      toast({
        title: 'Ticket movido',
        description: `Ticket movido para "${targetStage.name}"`
      });
    } catch (error: any) {
      console.error('❌ [Tickets] Erro ao mover ticket:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || error.message || 'Erro ao mover ticket',
        variant: 'destructive'
      });
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.client_id || newTicket.client_id === 0 ||
      !newTicket.reason_id || newTicket.reason_id === 0 ||
      !newTicket.title || !newTicket.description) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos obrigatórios',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      // Preparar dados para envio (remover campos undefined)
      const ticketData: CreateTicketDTO = {
        client_id: newTicket.client_id,
        reason_id: newTicket.reason_id,
        title: newTicket.title.trim(),
        description: newTicket.description.trim(),
        priority: newTicket.priority || TicketPriority.MEDIUM
      };

      if (newTicket.assigned_to) {
        ticketData.assigned_to = newTicket.assigned_to;
      }

      if (newTicket.due_date) {
        ticketData.due_date = newTicket.due_date;
      }

      if (newTicket.co_assignees && newTicket.co_assignees.length > 0) {
        ticketData.co_assignees = newTicket.co_assignees;
      }

      const createdTicket = await ticketService.createTicket(ticketData);

      // Upload dos anexos selecionados na criação (vinculados ao ticket, não ao chat)
      if (createdTicket?.id && newTicketFiles.length > 0) {
        for (const file of newTicketFiles) {
          try {
            await ticketService.uploadAttachment(createdTicket.id, file, { asMessage: false });
          } catch (uploadError) {
            console.error('Erro ao enviar anexo do ticket:', uploadError);
            toast({
              title: 'Anexo não enviado',
              description: `Falha ao enviar "${file.name}". O ticket foi criado mesmo assim.`,
              variant: 'destructive'
            });
          }
        }
      }

      toast({
        title: 'Ticket criado',
        description: 'Ticket criado com sucesso'
      });
      setShowCreateModal(false);
      setNewTicket({
        client_id: 0,
        reason_id: 0,
        title: '',
        description: '',
        priority: TicketPriority.MEDIUM,
        due_date: undefined,
        co_assignees: [] as number[],
      });
      setNewTicketFiles([]);
      await loadData();
    } catch (error: any) {
      console.error('Erro ao criar ticket:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || error.message || 'Erro ao criar ticket',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTicketClick = async (ticket: Ticket) => {
    try {
      // Buscar dados mais recentes do ticket antes de abrir o modal
      const updatedTicket = await ticketService.getTicket(ticket.id);
      setSelectedTicket(updatedTicket);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('[Tickets] Erro ao buscar ticket atualizado:', error);
      // Em caso de erro, usar o ticket da lista mesmo
      setSelectedTicket(ticket);
      setShowDetailsModal(true);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Tickets</h1>
            <p className="text-muted-foreground">Gerencie chamados e suporte</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReportsModal(true)}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Relatórios
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSettingsModal(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Configurações
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Ticket
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por #ID, título, cliente ou motivo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <TicketFilterMenu
                filter={globalFilter}
                onChange={setGlobalFilter}
                onClear={handleClearAllFilters}
                clearVisible={isFilterActive(globalFilter) || anyColumnFilterActive}
              />
              <TicketNotificationsBell
                onOpenTicket={(ticketId) => {
                  // Marca como visto e avisa sininho + sidebar para atualizarem o badge na hora.
                  ticketService.markAsViewed(ticketId)
                    .then(() => emitTicketNotificationsChanged())
                    .catch(() => {});
                  ticketService.getTicket(ticketId)
                    .then((t) => { setSelectedTicket(t); setShowDetailsModal(true); })
                    .catch((e) => console.error('[Tickets] Erro ao abrir ticket da notificação:', e));
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Kanban Board */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        ) : (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div className="flex gap-4 overflow-x-auto pb-4">
              {stages.map((stage) => {
                // Filtrar tickets por stage_id usando comparação numérica robusta
                // Garantir que ambos sejam convertidos para Number para evitar problemas de tipo
                const stageTickets = tickets.filter(t => {
                  if (!t) return false;
                  // Garantir que stage_id existe e é válido
                  if (t.stage_id === null || t.stage_id === undefined) return false;
                  // Comparação numérica robusta - garantir que ambos sejam números
                  const ticketStageId = Number(t.stage_id);
                  const stageIdNum = Number(stage.id);
                  return !isNaN(ticketStageId) && !isNaN(stageIdNum) && ticketStageId === stageIdNum;
                });

                console.log(`📊 [Tickets] Coluna "${stage.name}":`, {
                  stageId: stage.id,
                  stageIdType: typeof stage.id,
                  ticketsCount: stageTickets.length,
                  ticketIds: stageTickets.map(t => t.id),
                  ticketStageIds: stageTickets.map(t => ({
                    id: t.id,
                    stage_id: t.stage_id,
                    stageIdType: typeof t.stage_id,
                    title: t.title?.substring(0, 30)
                  })),
                  totalTicketsInState: tickets.length,
                });

                return (
                  <TicketKanbanColumn
                    key={stage.id}
                    stage={stage}
                    tickets={stageTickets}
                    onTicketClick={handleTicketClick}
                    searchTerm={searchTerm}
                    currentUserId={currentUser?.id || null}
                    globalFilter={globalFilter}
                    filter={columnFilters[stage.id] ?? EMPTY_TICKET_FILTER}
                    onFilterChange={(f) =>
                      setColumnFilters(prev => ({ ...prev, [stage.id]: f }))
                    }
                  />
                );
              })}
            </div>
          </DndContext>
        )}

        {/* Modal de Criar Ticket */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Novo Ticket</DialogTitle>
              <DialogDescription>Criar um novo chamado de suporte</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Cliente *</Label>
                <Select
                  value={newTicket.client_id && newTicket.client_id > 0 ? newTicket.client_id.toString() : undefined}
                  onValueChange={(value) =>
                    setNewTicket({ ...newTicket, client_id: Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id.toString()}>
                        {client.legal_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Motivo *</Label>
                <Select
                  value={newTicket.reason_id && newTicket.reason_id > 0 ? newTicket.reason_id.toString() : undefined}
                  onValueChange={(value) =>
                    setNewTicket({ ...newTicket, reason_id: Number(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id.toString()}>
                        {reason.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Título *</Label>
                <Input
                  value={newTicket.title}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, title: e.target.value })
                  }
                  placeholder="Resumo do problema"
                />
              </div>

              <div>
                <Label>Descrição *</Label>
                <Textarea
                  value={newTicket.description}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, description: e.target.value })
                  }
                  placeholder="Descreva o problema em detalhes"
                  rows={5}
                />
              </div>

              <div>
                <Label>Anexos (opcional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Imagens, vídeos ou documentos para ajudar a visualizar o problema.
                </p>
                <input
                  id="new-ticket-files"
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      setNewTicketFiles((prev) => [...prev, ...files]);
                    }
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('new-ticket-files')?.click()}
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Adicionar arquivos
                </Button>
                {newTicketFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {newTicketFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5 text-sm"
                      >
                        <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate flex-1">{file.name}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {(file.size / 1024 / 1024).toFixed(1)} MB
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setNewTicketFiles((prev) => prev.filter((_, i) => i !== index))
                          }
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Remover anexo"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prioridade</Label>
                  <Select
                    value={newTicket.priority}
                    onValueChange={(value) =>
                      setNewTicket({ ...newTicket, priority: value as TicketPriority })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TicketPriority.LOW}>Baixa</SelectItem>
                      <SelectItem value={TicketPriority.MEDIUM}>Média</SelectItem>
                      <SelectItem value={TicketPriority.HIGH}>Alta</SelectItem>
                      <SelectItem value={TicketPriority.URGENT}>Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Atribuir para</Label>
                  <Select
                    value={newTicket.assigned_to?.toString() || undefined}
                    onValueChange={(value) =>
                      setNewTicket({
                        ...newTicket,
                        assigned_to: value ? Number(value) : undefined
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Não atribuir" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Co-responsáveis (opcional)</Label>
                <MultiUserSelect
                  users={users}
                  selectedIds={newTicket.co_assignees ?? []}
                  excludedIds={(newTicket as any).assigned_to ? [(newTicket as any).assigned_to] : []}
                  onChange={(ids) => setNewTicket({ ...newTicket, co_assignees: ids })}
                  placeholder="Adicionar co-responsável"
                />
                <p className="text-xs text-muted-foreground">
                  Quem mais recebe este ticket na bandeja além do responsável principal.
                </p>
              </div>

              <div>
                <Label>Prazo de Conclusão (opcional)</Label>
                <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {newTicket.due_date
                        ? format(new Date(newTicket.due_date + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })
                        : 'Selecionar data'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={newTicket.due_date ? new Date(newTicket.due_date + 'T00:00:00') : undefined}
                      onSelect={(d) => {
                        setNewTicket({
                          ...newTicket,
                          due_date: d ? format(d, 'yyyy-MM-dd') : undefined,
                        });
                        setDueDateOpen(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                {newTicket.due_date && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline mt-1 hover:text-destructive"
                    onClick={() => setNewTicket({ ...newTicket, due_date: undefined })}
                  >
                    Limpar data
                  </button>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateTicket} disabled={isLoading}>
                  Criar Ticket
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Detalhes */}
        <TicketDetailsModal
          ticket={selectedTicket}
          open={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTicket(null);
          }}
          onUpdate={async () => {
            // Recarregar todos os dados primeiro
            await loadData();
            // Atualizar o selectedTicket com os dados mais recentes
            // Buscar o ticket atualizado após recarregar os dados
            const currentTicketId = selectedTicket?.id;
            if (currentTicketId) {
              try {
                const updatedTicket = await ticketService.getTicket(currentTicketId);
                console.log('[Tickets] Ticket atualizado recebido:', {
                  id: updatedTicket.id,
                  assigned_to: updatedTicket.assigned_to,
                  assigned_to_type: typeof updatedTicket.assigned_to
                });
                setSelectedTicket(updatedTicket);
              } catch (error) {
                console.error('[Tickets] Erro ao atualizar selectedTicket:', error);
              }
            }
          }}
          onDelete={async () => {
            // Quando o ticket for deletado, recarregar dados e fechar o modal
            setShowDetailsModal(false);
            setSelectedTicket(null);
            await loadData();
          }}
          stages={stages}
          reasons={reasons}
          users={users}
        />

        {/* Modal de Configurações */}
        <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Configurações</DialogTitle>
              <DialogDescription>Gerenciar etapas e motivos do sistema de tickets</DialogDescription>
            </DialogHeader>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="stages">Etapas</TabsTrigger>
                <TabsTrigger value="reasons">Motivos</TabsTrigger>
              </TabsList>

              <TabsContent value="stages" className="space-y-4 mt-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium">Etapas do Kanban</h3>
                  <Button
                    onClick={() => {
                      setEditingStage(null);
                      setStageForm({ name: '', description: '', color: '#3B82F6', order_index: stages.length });
                      setShowStageForm(true);
                    }}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Etapa
                  </Button>
                </div>

                <div className="space-y-2">
                  {stages.map((stage) => (
                    <Card key={stage.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-5 h-5 rounded border-2 border-gray-200"
                            style={{ backgroundColor: stage.color }}
                          />
                          <div>
                            <div className="font-medium">{stage.name}</div>
                            {stage.description && (
                              <div className="text-sm text-muted-foreground">{stage.description}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">Ordem: {stage.order_index}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingStage(stage);
                              setStageForm({
                                name: stage.name,
                                description: stage.description || '',
                                color: stage.color,
                                order_index: stage.order_index
                              });
                              setShowStageForm(true);
                            }}
                          >
                            Editar
                          </Button>
                          {!stage.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (confirm(`Deseja realmente deletar a etapa "${stage.name}"?`)) {
                                  try {
                                    await ticketService.deleteStage(stage.id);
                                    toast({
                                      title: 'Etapa deletada',
                                      description: 'Etapa deletada com sucesso'
                                    });
                                    await loadData();
                                  } catch (error: any) {
                                    toast({
                                      title: 'Erro',
                                      description: error.response?.data?.error || 'Erro ao deletar etapa',
                                      variant: 'destructive'
                                    });
                                  }
                                }
                              }}
                            >
                              Deletar
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="reasons" className="space-y-4 mt-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium">Motivos de Tickets</h3>
                  <Button
                    onClick={() => {
                      setEditingReason(null);
                      setReasonForm({ name: '', description: '' });
                      setShowReasonForm(true);
                    }}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Motivo
                  </Button>
                </div>

                <div className="space-y-2">
                  {reasons.map((reason) => (
                    <Card key={reason.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{reason.name}</div>
                          {reason.description && (
                            <div className="text-sm text-muted-foreground">{reason.description}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingReason(reason);
                              setReasonForm({
                                name: reason.name,
                                description: reason.description || ''
                              });
                              setShowReasonForm(true);
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              if (confirm(`Deseja realmente deletar o motivo "${reason.name}"?`)) {
                                try {
                                  await ticketService.deleteReason(reason.id);
                                  toast({
                                    title: 'Motivo deletado',
                                    description: 'Motivo deletado com sucesso'
                                  });
                                  await loadData();
                                } catch (error: any) {
                                  toast({
                                    title: 'Erro',
                                    description: error.response?.data?.error || 'Erro ao deletar motivo',
                                    variant: 'destructive'
                                  });
                                }
                              }
                            }}
                          >
                            Deletar
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Modal de Criar/Editar Etapa */}
        <Dialog open={showStageForm} onOpenChange={setShowStageForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingStage ? 'Editar Etapa' : 'Nova Etapa'}</DialogTitle>
              <DialogDescription>
                {editingStage ? 'Atualize os dados da etapa' : 'Crie uma nova etapa para o Kanban'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={stageForm.name}
                  onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
                  placeholder="Ex: Em Análise"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={stageForm.description}
                  onChange={(e) => setStageForm({ ...stageForm, description: e.target.value })}
                  placeholder="Descrição da etapa"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cor</Label>
                  <Input
                    type="color"
                    value={stageForm.color}
                    onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label>Ordem</Label>
                  <Input
                    type="number"
                    value={stageForm.order_index}
                    onChange={(e) => setStageForm({ ...stageForm, order_index: Number(e.target.value) })}
                    min="0"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowStageForm(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    if (!stageForm.name) {
                      toast({
                        title: 'Erro',
                        description: 'Nome é obrigatório',
                        variant: 'destructive'
                      });
                      return;
                    }
                    try {
                      if (editingStage) {
                        await ticketService.updateStage(editingStage.id, stageForm);
                        toast({
                          title: 'Etapa atualizada',
                          description: 'Etapa atualizada com sucesso'
                        });
                      } else {
                        await ticketService.createStage(stageForm);
                        toast({
                          title: 'Etapa criada',
                          description: 'Etapa criada com sucesso'
                        });
                      }
                      setShowStageForm(false);
                      await loadData();
                    } catch (error: any) {
                      toast({
                        title: 'Erro',
                        description: error.response?.data?.error || 'Erro ao salvar etapa',
                        variant: 'destructive'
                      });
                    }
                  }}
                >
                  Salvar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Criar/Editar Motivo */}
        <Dialog open={showReasonForm} onOpenChange={setShowReasonForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingReason ? 'Editar Motivo' : 'Novo Motivo'}</DialogTitle>
              <DialogDescription>
                {editingReason ? 'Atualize os dados do motivo' : 'Crie um novo motivo para tickets'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={reasonForm.name}
                  onChange={(e) => setReasonForm({ ...reasonForm, name: e.target.value })}
                  placeholder="Ex: ChatConnect"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={reasonForm.description}
                  onChange={(e) => setReasonForm({ ...reasonForm, description: e.target.value })}
                  placeholder="Descrição do motivo"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowReasonForm(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    if (!reasonForm.name) {
                      toast({
                        title: 'Erro',
                        description: 'Nome é obrigatório',
                        variant: 'destructive'
                      });
                      return;
                    }
                    try {
                      if (editingReason) {
                        await ticketService.updateReason(editingReason.id, reasonForm);
                        toast({
                          title: 'Motivo atualizado',
                          description: 'Motivo atualizado com sucesso'
                        });
                      } else {
                        await ticketService.createReason(reasonForm);
                        toast({
                          title: 'Motivo criado',
                          description: 'Motivo criado com sucesso'
                        });
                      }
                      setShowReasonForm(false);
                      await loadData();
                    } catch (error: any) {
                      toast({
                        title: 'Erro',
                        description: error.response?.data?.error || 'Erro ao salvar motivo',
                        variant: 'destructive'
                      });
                    }
                  }}
                >
                  Salvar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Relatórios */}
        <Dialog
          open={showReportsModal}
          onOpenChange={(open) => {
            setShowReportsModal(open);
            if (open) {
              // Carregar métricas automaticamente ao abrir o modal
              const loadMetrics = async () => {
                try {
                  setIsLoadingMetrics(true);
                  const params: any = {};
                  if (reportFilters.start_date) params.start_date = reportFilters.start_date;
                  if (reportFilters.end_date) params.end_date = reportFilters.end_date;
                  if (reportFilters.reason_id && reportFilters.reason_id !== 'all') params.reason_id = Number(reportFilters.reason_id);
                  if (reportFilters.client_id && reportFilters.client_id !== 'all') params.client_id = Number(reportFilters.client_id);

                  const data = await ticketService.getMetrics(params);
                  setMetrics(data);
                } catch (error: any) {
                  console.error('Erro ao carregar métricas:', error);
                } finally {
                  setIsLoadingMetrics(false);
                }
              };
              loadMetrics();
            }
          }}
        >
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Relatórios e Métricas</DialogTitle>
              <DialogDescription>Análise de performance dos tickets</DialogDescription>
            </DialogHeader>

            {/* Filtros */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border rounded-lg bg-muted/50">
              <div>
                <Label className="text-xs">Data Inicial</Label>
                <Input
                  type="date"
                  value={reportFilters.start_date}
                  onChange={(e) => setReportFilters({ ...reportFilters, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Data Final</Label>
                <Input
                  type="date"
                  value={reportFilters.end_date}
                  onChange={(e) => setReportFilters({ ...reportFilters, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Motivo</Label>
                <Select
                  value={reportFilters.reason_id || 'all'}
                  onValueChange={(value) => setReportFilters({ ...reportFilters, reason_id: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {reasons.map(reason => (
                      <SelectItem key={reason.id} value={reason.id.toString()}>
                        {reason.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={async () => {
                    try {
                      setIsLoadingMetrics(true);
                      const params: any = {};
                      if (reportFilters.start_date) params.start_date = reportFilters.start_date;
                      if (reportFilters.end_date) params.end_date = reportFilters.end_date;
                      if (reportFilters.reason_id && reportFilters.reason_id !== 'all') params.reason_id = Number(reportFilters.reason_id);
                      if (reportFilters.client_id && reportFilters.client_id !== 'all') params.client_id = Number(reportFilters.client_id);

                      const data = await ticketService.getMetrics(params);
                      setMetrics(data);
                    } catch (error: any) {
                      toast({
                        title: 'Erro',
                        description: 'Erro ao carregar métricas',
                        variant: 'destructive'
                      });
                    } finally {
                      setIsLoadingMetrics(false);
                    }
                  }}
                  disabled={isLoadingMetrics}
                  className="w-full"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {isLoadingMetrics ? 'Carregando...' : 'Aplicar Filtros'}
                </Button>
              </div>
            </div>

            {/* Métricas */}
            {isLoadingMetrics ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Carregando métricas...</p>
              </div>
            ) : metrics ? (
              <div className="space-y-6">
                {/* Cards de Métricas Principais */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total de Tickets</CardTitle>
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{metrics.total_tickets}</div>
                      <p className="text-xs text-muted-foreground">Tickets no período</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Tempo Médio</CardTitle>
                      <Clock className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {metrics.avg_resolution_hours > 0
                          ? `${metrics.avg_resolution_hours.toFixed(1)}h`
                          : 'N/A'}
                      </div>
                      <p className="text-xs text-muted-foreground">Tempo médio de resolução</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Por Motivo</CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{metrics.by_reason.length}</div>
                      <p className="text-xs text-muted-foreground">Categorias diferentes</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Gráfico de Tickets por Motivo */}
                {metrics.by_reason.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Tickets por Motivo</CardTitle>
                      <CardDescription>Distribuição de tickets por categoria</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ChartContainer
                        config={{
                          count: { label: 'Tickets' }
                        }}
                        className="h-[300px]"
                      >
                        <BarChart data={metrics.by_reason}>
                          <XAxis
                            dataKey="reason_name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                          />
                          <YAxis />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="count" fill="hsl(var(--primary))" />
                        </BarChart>
                      </ChartContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Tabela Detalhada */}
                {metrics.by_reason.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Detalhamento por Motivo</CardTitle>
                      <CardDescription>Estatísticas detalhadas por categoria</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Motivo</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="text-right">Tempo Médio</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {metrics.by_reason.map((item) => (
                            <TableRow key={item.reason_id}>
                              <TableCell className="font-medium">{item.reason_name}</TableCell>
                              <TableCell className="text-right">{item.count}</TableCell>
                              <TableCell className="text-right">
                                {item.avg_resolution_hours > 0
                                  ? `${item.avg_resolution_hours.toFixed(1)}h`
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-12 border rounded-lg">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Selecione os filtros e clique em "Aplicar Filtros" para ver as métricas
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Tickets;

